import { useState, useEffect } from 'react';
import { getUserErrorMessage } from '../../utils/errorMessages';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import MainLayout from '../../components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { 
  Loader2,
  Package,
  Check,
  Clock,
  AlertCircle,
  Euro,
  Calendar,
  Layers,
  ArrowRight,
  X,
  Sparkles,
  Info,
  Minus,
  FileCheck,
  Shield,
  Scale,
  Cpu
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import AuditLogPanel from '../../components/AuditLogPanel';

// Module descriptions
const MODULE_DESCRIPTIONS = {
  'nulmeting': 'Voer een baseline security assessment uit over mensen, processen en technologie',
  'strategie': 'Stel uw cybersecurity strategie op en beheer doelstellingen en roadmap',
  'risicobeheer': 'Identificeer, beoordeel en prioriteer risico\'s met een risicoregister en heatmap',
  'maatregelen': 'Beheer beveiligingsmaatregelen en volg implementatievoortgang',
  'kwetsbaarheden': 'Scan op kwetsbaarheden en volg remediatie-acties',
  'bedreigingen': 'Monitor dreigingen en analyseer intent vs. capaciteit van dreigingsactoren',
  'assets': 'Registreer en classificeer uw bedrijfsmiddelen en kroonjuwelen',
  'leveranciers': 'Beoordeel en monitor de security van uw leveranciers en ketenpartners',
  'rapportage': 'Genereer compliance-rapporten en exporteer gegevens naar PDF',
  'phishing': 'Voer phishing-simulaties uit om het bewustzijn van medewerkers te testen',
  'scanning': 'Automatische security scans op uw domeinen en infrastructuur',
  'api': 'Toegang tot de Spooker API voor integratie met externe systemen',
  'sso': 'Single Sign-On integratie met uw identity provider'
};

const SubscriptionPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [availableTiers, setAvailableTiers] = useState([]);
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [requestNotes, setRequestNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showComparison, setShowComparison] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, tiersRes] = await Promise.all([
        api.get('/subscriptions/my-subscription'),
        api.get('/subscriptions/available-tiers')
      ]);
      setSubscription(subRes.data);
      setAvailableTiers(tiersRes.data.tiers || []);
    } catch (error) {
      toast.error('Uw abonnementsgegevens konden niet worden geladen. Ververs de pagina.');
    } finally {
      setLoading(false);
    }
  };

  const openRequestDialog = (tier) => {
    setSelectedTier(tier);
    setRequestNotes('');
    setRequestDialogOpen(true);
  };

  const handleRequestTier = async () => {
    if (!selectedTier) return;
    
    setSubmitting(true);
    try {
      await api.post('/subscriptions/request', {
        tier_id: selectedTier.id,
        notes: requestNotes || null
      });
      toast.success('Aanvraag ingediend! U ontvangt bericht zodra deze is goedgekeurd.');
      setRequestDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(getUserErrorMessage(error, 'De aanvraag kon niet worden verstuurd. Probeer het later opnieuw.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!subscription?.pending_request) return;
    
    try {
      await api.delete(`/subscriptions/request/${subscription.pending_request.id}`);
      toast.success('Aanvraag geannuleerd');
      fetchData();
    } catch (error) {
      toast.error('De annulering kon niet worden verwerkt. Probeer het later opnieuw.');
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center h-[calc(100vh-4rem)]">
          <Loader2 className="h-8 w-8 animate-spin text-neutral-400" />
        </div>
      </MainLayout>
    );
  }

  const currentTier = subscription?.current_tier;
  const pendingRequest = subscription?.pending_request;

  return (
    <MainLayout>
      <div className="p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-black" data-testid="subscription-page-title">
              Abonnement
            </h1>
            <p className="text-sm text-neutral-500 mt-1">
              Vergelijk pakketten, upgrade uw plan of activeer extra modules
            </p>
          </div>
          <AuditLogPanel entityType="subscription" buttonVariant="outline" className="border-neutral-200" />
        </div>

        <Tabs defaultValue="huidig" className="space-y-4">
          <TabsList className="w-full justify-start bg-neutral-100 p-1 rounded-lg h-auto flex-wrap">
            <TabsTrigger value="huidig" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5" data-testid="tab-huidig">
              <Package className="h-3.5 w-3.5" strokeWidth={1.5} />
              Huidig Abonnement
            </TabsTrigger>
            <TabsTrigger value="pakketten" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5" data-testid="tab-pakketten">
              <Layers className="h-3.5 w-3.5" strokeWidth={1.5} />
              Pakketten
            </TabsTrigger>
            <TabsTrigger value="modules" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5" data-testid="tab-modules">
              <Cpu className="h-3.5 w-3.5" strokeWidth={1.5} />
              Modules
            </TabsTrigger>
            <TabsTrigger value="uitbreidingen" className="rounded-md px-4 py-1.5 text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm flex items-center gap-1.5" data-testid="tab-uitbreidingen">
              <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />
              Uitbreidingen
            </TabsTrigger>
          </TabsList>

          {/* === TAB: Huidig Abonnement === */}
          <TabsContent value="huidig" className="space-y-6">
            {/* Current Subscription */}
            <Card className="border-neutral-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Package className="h-5 w-5" />
                  Huidig Abonnement
                </CardTitle>
              </CardHeader>
              <CardContent>
                {currentTier ? (
                  <div className="flex items-start justify-between">
                    <div className="space-y-3">
                      <div>
                        <h3 className="text-xl font-bold text-black">{currentTier.name}</h3>
                        {currentTier.description && (
                          <p className="text-sm text-neutral-600 mt-1">{currentTier.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-sm">
                        <div className="flex items-center gap-1.5">
                          <Euro className="h-4 w-4 text-neutral-400" />
                          <span className="font-medium">{currentTier.price?.toFixed(2)}</span>
                          <span className="text-neutral-500">/ {currentTier.billing_cycle === 'monthly' ? 'maand' : 'jaar'}</span>
                        </div>
                        {subscription.subscription_end_date && (
                          <div className="flex items-center gap-1.5 text-neutral-500">
                            <Calendar className="h-4 w-4" />
                            Verloopt: {new Date(subscription.subscription_end_date).toLocaleDateString('nl-NL')}
                          </div>
                        )}
                      </div>
                      {currentTier.modules?.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {currentTier.modules.map((moduleId) => (
                            <Badge key={moduleId} variant="outline" className="text-xs">
                              <Check className="h-3 w-3 mr-1 text-green-500" />
                              {moduleId}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                    <Badge className="bg-green-100 text-green-700"><Check className="h-3 w-3 mr-1" />Actief</Badge>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Package className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                    <p className="text-neutral-500 mb-2">Geen actief abonnement</p>
                    <p className="text-sm text-neutral-400">Kies een abonnement onder het tabblad "Pakketten".</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pending Request */}
            {pendingRequest && (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-3">
                      <Clock className="h-5 w-5 text-amber-600 mt-0.5" />
                      <p className="font-medium text-amber-800">Aanvraag voor <strong>{pendingRequest.requested_tier_name}</strong></p>
                    </div>
                    <Button variant="ghost" size="sm" className="text-amber-700 hover:text-amber-800 hover:bg-amber-100" onClick={handleCancelRequest} data-testid="cancel-request-button">
                      <X className="h-4 w-4 mr-1" />Annuleren
                    </Button>
                  </div>
                  <div className="flex items-center gap-0 ml-2" data-testid="request-timeline">
                    <div className="flex flex-col items-center">
                      <div className="h-7 w-7 rounded-full bg-green-600 flex items-center justify-center"><Check className="h-4 w-4 text-white" strokeWidth={2} /></div>
                      <p className="text-[10px] text-green-700 mt-1 font-medium">Ingediend</p>
                    </div>
                    <div className="flex-1 h-0.5 bg-amber-300 -mt-4" />
                    <div className="flex flex-col items-center">
                      <div className="h-7 w-7 rounded-full bg-amber-400 flex items-center justify-center animate-pulse"><Clock className="h-4 w-4 text-white" strokeWidth={2} /></div>
                      <p className="text-[10px] text-amber-700 mt-1 font-medium">In beoordeling</p>
                    </div>
                    <div className="flex-1 h-0.5 bg-neutral-200 -mt-4" />
                    <div className="flex flex-col items-center">
                      <div className="h-7 w-7 rounded-full bg-neutral-200 flex items-center justify-center"><FileCheck className="h-4 w-4 text-neutral-400" strokeWidth={2} /></div>
                      <p className="text-[10px] text-neutral-400 mt-1">Goedgekeurd</p>
                    </div>
                    <div className="flex-1 h-0.5 bg-neutral-200 -mt-4" />
                    <div className="flex flex-col items-center">
                      <div className="h-7 w-7 rounded-full bg-neutral-200 flex items-center justify-center"><Check className="h-4 w-4 text-neutral-400" strokeWidth={2} /></div>
                      <p className="text-[10px] text-neutral-400 mt-1">Actief</p>
                    </div>
                  </div>
                  <p className="text-xs text-amber-600 mt-3 ml-2">Gemiddelde doorlooptijd: 1-2 werkdagen.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* === TAB: Pakketten === */}
          <TabsContent value="pakketten" className="space-y-6">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-base font-bold text-black">Beschikbare Abonnementen</h2>
              {availableTiers.length > 1 && (
                <Button variant="outline" size="sm" className="text-xs" onClick={() => setShowComparison(!showComparison)} data-testid="toggle-comparison">
                  {showComparison ? 'Kaarten weergave' : 'Vergelijk pakketten'}
                </Button>
              )}
            </div>

          {availableTiers.length > 0 ? (
            <>
              {/* Comparison Table */}
              {showComparison ? (
                <Card className="border-neutral-200 overflow-hidden" data-testid="comparison-table">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-neutral-50 border-b border-neutral-200">
                          <th className="text-left p-4 font-medium text-neutral-500 w-1/4">Kenmerk</th>
                          {availableTiers.map(tier => (
                            <th key={tier.id} className={`text-center p-4 font-semibold text-black ${currentTier?.id === tier.id ? 'bg-black/5' : ''}`}>
                              {tier.name}
                              {currentTier?.id === tier.id && (
                                <Badge className="ml-2 bg-black text-white text-[10px]">Huidig</Badge>
                              )}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-neutral-100">
                        <tr>
                          <td className="p-4 text-neutral-600 font-medium">Prijs</td>
                          {availableTiers.map(tier => (
                            <td key={tier.id} className={`text-center p-4 ${currentTier?.id === tier.id ? 'bg-black/5' : ''}`}>
                              <span className="text-lg font-bold">&euro;{tier.price?.toFixed(0)}</span>
                              <span className="text-neutral-500 text-xs"> / {tier.billing_cycle === 'monthly' ? 'mnd' : 'jr'}</span>
                            </td>
                          ))}
                        </tr>
                        {/* Features comparison */}
                        {(() => {
                          const allFeatures = [...new Set(availableTiers.flatMap(t => t.features || []))];
                          return allFeatures.map((feature, i) => (
                            <tr key={`feature-${feature}`}>
                              <td className="p-4 text-neutral-600 text-xs">{feature}</td>
                              {availableTiers.map(tier => (
                                <td key={tier.id} className={`text-center p-4 ${currentTier?.id === tier.id ? 'bg-black/5' : ''}`}>
                                  {tier.features?.includes(feature) ? (
                                    <Check className="h-4 w-4 text-green-500 mx-auto" />
                                  ) : (
                                    <Minus className="h-4 w-4 text-neutral-300 mx-auto" />
                                  )}
                                </td>
                              ))}
                            </tr>
                          ));
                        })()}
                        {/* Modules comparison */}
                        {(() => {
                          const allModules = [...new Set(availableTiers.flatMap(t => t.modules || []))];
                          return allModules.map((mod, i) => (
                            <tr key={`mod-${i}`}>
                              <td className="p-4 text-neutral-600 text-xs">
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <span className="inline-flex items-center gap-1 cursor-help">
                                        {mod} <Info className="h-3 w-3 text-neutral-400" />
                                      </span>
                                    </TooltipTrigger>
                                    <TooltipContent side="right" className="max-w-[220px] text-xs">
                                      {MODULE_DESCRIPTIONS[mod.toLowerCase()] || `Module: ${mod}`}
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </td>
                              {availableTiers.map(tier => (
                                <td key={tier.id} className={`text-center p-4 ${currentTier?.id === tier.id ? 'bg-black/5' : ''}`}>
                                  {tier.modules?.includes(mod) ? (
                                    <Check className="h-4 w-4 text-green-500 mx-auto" />
                                  ) : (
                                    <Minus className="h-4 w-4 text-neutral-300 mx-auto" />
                                  )}
                                </td>
                              ))}
                            </tr>
                          ));
                        })()}
                        {/* Action Row */}
                        <tr>
                          <td className="p-4"></td>
                          {availableTiers.map(tier => {
                            const isCurrentTier = currentTier?.id === tier.id;
                            const isPendingTier = pendingRequest?.requested_tier_id === tier.id;
                            return (
                              <td key={tier.id} className={`text-center p-4 ${isCurrentTier ? 'bg-black/5' : ''}`}>
                                {isCurrentTier ? (
                                  <Badge className="bg-green-100 text-green-700">Huidig</Badge>
                                ) : isPendingTier ? (
                                  <Badge className="bg-amber-100 text-amber-700">In behandeling</Badge>
                                ) : pendingRequest ? (
                                  <Button size="sm" variant="outline" disabled className="text-xs">Aanvraag al ingediend</Button>
                                ) : (
                                  <Button size="sm" className="text-xs" onClick={() => openRequestDialog(tier)} data-testid={`compare-select-${tier.id}`}>
                                    Selecteren
                                  </Button>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </Card>
              ) : (
                /* Card View */
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {availableTiers.map((tier) => {
                    const isCurrentTier = currentTier?.id === tier.id;
                    const isPendingTier = pendingRequest?.requested_tier_id === tier.id;
                    
                    return (
                      <Card 
                        key={tier.id} 
                        className={`border-neutral-200 dark:border-neutral-800 relative flex flex-col ${
                          isCurrentTier ? 'ring-2 ring-black dark:ring-white' : ''
                        }`}
                        data-testid={`tier-card-${tier.id}`}
                      >
                        {isCurrentTier && (
                          <div className="absolute -top-3 left-4">
                            <Badge className="bg-black text-white dark:bg-white dark:text-black">
                              Huidig
                            </Badge>
                          </div>
                        )}
                        
                        <CardHeader className="pb-2">
                          <CardTitle className="text-lg">{tier.name}</CardTitle>
                          {tier.description && (
                            <CardDescription>{tier.description}</CardDescription>
                          )}
                        </CardHeader>
                        
                        <CardContent className="space-y-4 flex-1">
                          <div className="flex items-baseline gap-1">
                            <span className="text-3xl font-bold text-black dark:text-white">
                              &euro;{tier.price?.toFixed(0)}
                            </span>
                            <span className="text-neutral-500">
                              / {tier.billing_cycle === 'monthly' ? 'maand' : 'jaar'}
                            </span>
                          </div>

                          {tier.features?.length > 0 && (
                            <ul className="space-y-2">
                              {tier.features.map((feature, index) => (
                                <li key={`feat-${feature}`} className="flex items-start gap-2 text-sm">
                                  <Check className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span className="text-neutral-600 dark:text-neutral-400">{feature}</span>
                                </li>
                              ))}
                            </ul>
                          )}

                          {tier.modules?.length > 0 && (
                            <div>
                              <p className="text-xs text-neutral-500 mb-2">Inbegrepen modules:</p>
                              <div className="flex flex-wrap gap-1">
                                <TooltipProvider>
                                  {tier.modules.map((moduleId) => (
                                    <Tooltip key={moduleId}>
                                      <TooltipTrigger asChild>
                                        <div>
                                          <Badge variant="outline" className="text-xs cursor-help" data-testid={`module-badge-${moduleId}`}>
                                            {moduleId}
                                          </Badge>
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="top" className="max-w-[220px] text-xs">
                                        {MODULE_DESCRIPTIONS[moduleId.toLowerCase()] || `Module: ${moduleId}`}
                                      </TooltipContent>
                                    </Tooltip>
                                  ))}
                                </TooltipProvider>
                              </div>
                            </div>
                          )}
                        </CardContent>
                        
                        <CardFooter>
                          {isCurrentTier ? (
                            <Button className="w-full" disabled>
                              <Check className="h-4 w-4 mr-2" />
                              Huidig abonnement
                            </Button>
                          ) : isPendingTier ? (
                            <Button className="w-full" variant="outline" disabled>
                              <Clock className="h-4 w-4 mr-2" />
                              Aanvraag in behandeling
                            </Button>
                          ) : pendingRequest ? (
                            <Button className="w-full" variant="outline" disabled>
                              Aanvraag al ingediend
                            </Button>
                          ) : (
                            <Button 
                              className="w-full" 
                              onClick={() => openRequestDialog(tier)}
                              data-testid={`select-tier-${tier.id}`}
                            >
                              {currentTier ? 'Upgraden' : 'Selecteren'}
                              <ArrowRight className="h-4 w-4 ml-2" />
                            </Button>
                          )}
                        </CardFooter>
                      </Card>
                    );
                  })}
                </div>
              )}
            </>
          ) : (
            <Card className="border-neutral-200 dark:border-neutral-800">
              <CardContent className="py-12 text-center">
                <Layers className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
                <p className="text-neutral-500">Geen abonnementen beschikbaar</p>
              </CardContent>
            </Card>
          )}
          </TabsContent>

          {/* === TAB: Modules === */}
          <TabsContent value="modules" className="space-y-6" data-testid="modules-tab-content">
            {/* Basis-modules */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Shield className="h-5 w-5 text-emerald-500" strokeWidth={1.5} />
                  Basis-modules (Foundation Level)
                </CardTitle>
                <CardDescription className="text-xs text-neutral-500">
                  Alles wat u minimaal nodig heeft om compliant en veilig te zijn.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200">
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Module</th>
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Categorie</th>
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Beschrijving</th>
                        <th className="p-4 text-center text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {[
                        { name: 'AVG / GDPR', cat: 'Privacy', desc: 'Europese privacywetgeving' },
                        { name: 'ISO 27001', cat: 'Framework', desc: 'ISMS eisen voor informatiebeveiligingsbeheer' },
                        { name: 'ISO 27002', cat: 'Controls', desc: 'Best practices voor informatiebeveiligingscontroles' },
                        { name: 'NIS2 / Wbni / Cbw', cat: 'Wetgeving', desc: 'Europese richtlijn voor netwerk- en informatiebeveiliging' },
                        { name: 'BIO', cat: 'Overheid', desc: 'Baseline Informatiebeveiliging Overheid' },
                        { name: 'NEN 7510', cat: 'Zorg', desc: 'Informatiebeveiliging in de zorgsector' },
                        { name: 'OWASP Top 10', cat: 'Technisch', desc: 'Top 10 meest kritieke webapplicatie-kwetsbaarheden' },
                        { name: 'ePrivacy / Telecommunicatiewet', cat: 'Privacy', desc: 'Privacyregels voor elektronische communicatie' },
                        { name: 'Arbowet (digitale veiligheid)', cat: 'Wetgeving', desc: 'Arbeidsomstandigheden inclusief digitale veiligheid' },
                      ].map((m) => (
                        <tr key={m.name} className="hover:bg-neutral-50">
                          <td className="p-4"><span className="text-sm font-medium text-black">{m.name}</span></td>
                          <td className="p-4"><Badge variant="outline" className="text-[10px]">{m.cat}</Badge></td>
                          <td className="p-4"><span className="text-xs text-neutral-600">{m.desc}</span></td>
                          <td className="p-4 text-center"><Badge className="bg-emerald-100 text-emerald-700 text-[10px]">Beschikbaar</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Gevorderde modules */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Scale className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                  Gevorderde modules (Advanced Level)
                </CardTitle>
                <CardDescription className="text-xs text-neutral-500">
                  Voor organisaties die security structureel willen borgen en aantoonbaar volwassen willen worden.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200">
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Module</th>
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Categorie</th>
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Beschrijving</th>
                        <th className="p-4 text-center text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {[
                        { name: 'ISO 27005', cat: 'Risico', desc: 'Risicomanagement voor informatiebeveiliging' },
                        { name: 'ISO 27017', cat: 'Cloud', desc: 'Cloud security richtlijnen' },
                        { name: 'ISO 27018', cat: 'Cloud', desc: 'Cloud privacy bescherming van PII' },
                        { name: 'ISO 27035', cat: 'Incident', desc: 'Incidentmanagement procedures' },
                        { name: 'ISO 22301', cat: 'BCM', desc: 'Business Continuity Management' },
                        { name: 'ISO 31000', cat: 'Risico', desc: 'Algemeen risicomanagement framework' },
                        { name: 'SOC 2', cat: 'Audit', desc: 'Service Organization Control rapport' },
                        { name: 'COBIT 2019', cat: 'Governance', desc: 'IT governance en management framework' },
                        { name: 'COSO ERM', cat: 'Risico', desc: 'Enterprise Risk Management framework' },
                        { name: 'DORA', cat: 'Financieel', desc: 'Digital Operational Resilience Act' },
                        { name: 'CER-richtlijn', cat: 'Wetgeving', desc: 'Critical Entities Resilience richtlijn' },
                        { name: 'eIDAS / eIDAS2', cat: 'Identiteit', desc: 'Elektronische identificatie en vertrouwensdiensten' },
                        { name: 'PCI DSS', cat: 'Betaling', desc: 'Betalingskaart data beveiligingsstandaard' },
                        { name: 'CSA CCM', cat: 'Cloud', desc: 'Cloud Controls Matrix' },
                      ].map((m) => (
                        <tr key={m.name} className="hover:bg-neutral-50">
                          <td className="p-4"><span className="text-sm font-medium text-black">{m.name}</span></td>
                          <td className="p-4"><Badge variant="outline" className="text-[10px]">{m.cat}</Badge></td>
                          <td className="p-4"><span className="text-xs text-neutral-600">{m.desc}</span></td>
                          <td className="p-4 text-center"><Badge className="bg-blue-100 text-blue-700 text-[10px]">Beschikbaar</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Expert-modules */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-base font-semibold flex items-center gap-2">
                  <Cpu className="h-5 w-5 text-violet-500" strokeWidth={1.5} />
                  Expert-modules (Expert / Specialist Level)
                </CardTitle>
                <CardDescription className="text-xs text-neutral-500">
                  Voor kritieke sectoren die gespecialiseerde kennis of tooling vereisen.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-neutral-200">
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Module</th>
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Categorie</th>
                        <th className="p-4 text-left text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Beschrijving</th>
                        <th className="p-4 text-center text-xs font-bold uppercase tracking-wider text-neutral-400 bg-neutral-50">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-neutral-100">
                      {[
                        { name: 'Advanced Threat Intelligence', cat: 'Threat Intel', desc: 'Geavanceerde dreigingsinformatie en analyse' },
                        { name: 'MITRE ATT&CK', cat: 'Framework', desc: 'Adversarial Tactics, Techniques & Common Knowledge' },
                        { name: 'TIBER-EU / NL', cat: 'Testing', desc: 'Threat Intelligence Based Ethical Red teaming' },
                        { name: 'NIST SP 800-53', cat: 'Controls', desc: 'Security and Privacy Controls catalogus' },
                        { name: 'NIST CSF', cat: 'Framework', desc: 'Cybersecurity Framework' },
                        { name: 'ISA/IEC 62443', cat: 'OT/ICS', desc: 'Industrial Automation and Control Systems security' },
                        { name: 'FAIR', cat: 'Risico', desc: 'Factor Analysis of Information Risk (kwantitatief)' },
                        { name: 'ISO 27036', cat: 'Supply Chain', desc: 'Supply chain informatiebeveiligingsrelaties' },
                      ].map((m) => (
                        <tr key={m.name} className="hover:bg-neutral-50">
                          <td className="p-4"><span className="text-sm font-medium text-black">{m.name}</span></td>
                          <td className="p-4"><Badge variant="outline" className="text-[10px]">{m.cat}</Badge></td>
                          <td className="p-4"><span className="text-xs text-neutral-600">{m.desc}</span></td>
                          <td className="p-4 text-center"><Badge className="bg-violet-100 text-violet-700 text-[10px]">Beschikbaar</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* === TAB: Uitbreidingen === */}
          <TabsContent value="uitbreidingen" className="space-y-6" data-testid="tab-uitbreidingen-content">
            <p className="text-sm text-neutral-500">Extra functionaliteiten die u per pagina kunt activeren om uw cybersecurity governance uit te breiden.</p>

            {/* Dashboard uitbreidingen */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Layers className="h-4 w-4 text-blue-500" strokeWidth={1.5} /> Dashboard</CardTitle></CardHeader>
              <CardContent className="p-0"><div className="divide-y divide-neutral-100">
                {[
                  { name: 'Executive Summary PDF', desc: 'Genereer automatisch een management samenvatting als PDF-rapport', status: 'Binnenkort' },
                  { name: 'Custom KPI Widgets', desc: 'Configureer zelf welke KPI\'s op het dashboard worden getoond', status: 'Binnenkort' },
                ].map((e) => (
                  <div key={e.name} className="flex items-center justify-between px-4 py-3">
                    <div><p className="text-xs font-medium text-black">{e.name}</p><p className="text-[10px] text-neutral-500">{e.desc}</p></div>
                    <Badge className="bg-amber-50 text-amber-700 text-[10px] border border-amber-200">{e.status}</Badge>
                  </div>
                ))}
              </div></CardContent>
            </Card>

            {/* Nulmeting uitbreidingen */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><FileCheck className="h-4 w-4 text-green-500" strokeWidth={1.5} /> Nulmeting & Assessment</CardTitle></CardHeader>
              <CardContent className="p-0"><div className="divide-y divide-neutral-100">
                {[
                  { name: 'Benchmark Vergelijking', desc: 'Vergelijk uw score met branchegenoten (geanonimiseerd)', status: 'Binnenkort' },
                  { name: 'Automatische Aanbevelingen', desc: 'AI-gegenereerde aanbevelingen op basis van assessment resultaten', status: 'Binnenkort' },
                ].map((e) => (
                  <div key={e.name} className="flex items-center justify-between px-4 py-3">
                    <div><p className="text-xs font-medium text-black">{e.name}</p><p className="text-[10px] text-neutral-500">{e.desc}</p></div>
                    <Badge className="bg-amber-50 text-amber-700 text-[10px] border border-amber-200">{e.status}</Badge>
                  </div>
                ))}
              </div></CardContent>
            </Card>

            {/* Risico's uitbreidingen */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Shield className="h-4 w-4 text-red-500" strokeWidth={1.5} /> Risicobeheer</CardTitle></CardHeader>
              <CardContent className="p-0"><div className="divide-y divide-neutral-100">
                {[
                  { name: 'Kwantitatieve Risicoanalyse (FAIR)', desc: 'Monte Carlo simulaties voor financiële impact berekening', status: 'Binnenkort' },
                  { name: 'Risico Scenario Planning', desc: 'What-if analyses voor strategische besluitvorming', status: 'Binnenkort' },
                ].map((e) => (
                  <div key={e.name} className="flex items-center justify-between px-4 py-3">
                    <div><p className="text-xs font-medium text-black">{e.name}</p><p className="text-[10px] text-neutral-500">{e.desc}</p></div>
                    <Badge className="bg-amber-50 text-amber-700 text-[10px] border border-amber-200">{e.status}</Badge>
                  </div>
                ))}
              </div></CardContent>
            </Card>

            {/* Bedreigingen uitbreidingen */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Scale className="h-4 w-4 text-violet-500" strokeWidth={1.5} /> Bedreigingen & Intelligence</CardTitle></CardHeader>
              <CardContent className="p-0"><div className="divide-y divide-neutral-100">
                {[
                  { name: 'Live Threat Feed', desc: 'Real-time dreigingsinformatie van meerdere bronnen (OSINT, MISP)', status: 'Binnenkort' },
                  { name: 'IOC Management', desc: 'Indicators of Compromise bijhouden en koppelen aan uw assets', status: 'Binnenkort' },
                ].map((e) => (
                  <div key={e.name} className="flex items-center justify-between px-4 py-3">
                    <div><p className="text-xs font-medium text-black">{e.name}</p><p className="text-[10px] text-neutral-500">{e.desc}</p></div>
                    <Badge className="bg-amber-50 text-amber-700 text-[10px] border border-amber-200">{e.status}</Badge>
                  </div>
                ))}
              </div></CardContent>
            </Card>

            {/* Leveranciers uitbreidingen */}
            <Card className="border-neutral-200">
              <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold flex items-center gap-2"><Cpu className="h-4 w-4 text-orange-500" strokeWidth={1.5} /> Leveranciers & Supply Chain</CardTitle></CardHeader>
              <CardContent className="p-0"><div className="divide-y divide-neutral-100">
                {[
                  { name: 'Automatische Leveranciers Monitoring', desc: 'Continue monitoring van leveranciers op security incidenten en data breaches', status: 'Binnenkort' },
                  { name: 'SLA & Contract Tracking', desc: 'Beheer SLA\'s, contracten en verloopdatums van leveranciers', status: 'Binnenkort' },
                ].map((e) => (
                  <div key={e.name} className="flex items-center justify-between px-4 py-3">
                    <div><p className="text-xs font-medium text-black">{e.name}</p><p className="text-[10px] text-neutral-500">{e.desc}</p></div>
                    <Badge className="bg-amber-50 text-amber-700 text-[10px] border border-amber-200">{e.status}</Badge>
                  </div>
                ))}
              </div></CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Request Dialog */}
        <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
          <DialogContent data-testid="plan-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-yellow-500" />
                Abonnement Aanvragen
              </DialogTitle>
              <DialogDescription>
                U vraagt het <strong>{selectedTier?.name}</strong> abonnement aan voor €{selectedTier?.price?.toFixed(2)} per {selectedTier?.billing_cycle === 'monthly' ? 'maand' : 'jaar'}.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="p-4 bg-neutral-50 dark:bg-neutral-800/50 rounded-lg">
                <h4 className="font-medium text-black dark:text-white mb-2">
                  {selectedTier?.name}
                </h4>
                {selectedTier?.features?.length > 0 && (
                  <ul className="space-y-1">
                    {selectedTier.features.map((feature, index) => (
                      <li key={`feat-${feature}`} className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-400">
                        <Check className="h-3 w-3 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
                  Opmerkingen (optioneel)
                </label>
                <Textarea
                  value={requestNotes}
                  onChange={(e) => setRequestNotes(e.target.value)}
                  placeholder="Eventuele opmerkingen bij uw aanvraag..."
                  rows={3}
                />
              </div>

              <div className="flex items-start gap-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <p>
                  Uw aanvraag wordt beoordeeld door een beheerder. Na goedkeuring wordt uw abonnement geactiveerd en verschijnt de eerste factuur aan het einde van de maand.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setRequestDialogOpen(false)} data-testid="plan-cancel-button">
                Annuleren
              </Button>
              <Button onClick={handleRequestTier} disabled={submitting} data-testid="plan-confirm-button">
                {submitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Aanvraag Indienen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
};

export default SubscriptionPage;
